import { TokenService } from "./tokenService";
import { User, UserManager, UserManagerSettings } from "oidc-client";
import { IdentityAuthOptions, Profile, IdentityServerConfig, ClientInfo } from "./models";

type Permissions = string[];

export class IdSvrAuthService {
    public static componentName = "idSvrAuthService";

    private settings: IdentityAuthOptions;
    public userManager: UserManager | null = null;
    private permissions: Permissions = [];

    constructor(private tokenService: TokenService) {
        this.settings = {} as any;
    }

    public userLoaded(user: User) {
        this.tokenService.accessToken = user.access_token;
    }

    public signIn(): void {
        this.userManager?.signinSilent().catch(() => this.userManager?.signinRedirect({ data: window.location.href }));
    }

    public signOut(): void {
        this.tokenService.clear();

        if (window.top !== window.self) {
            window.top!.postMessage({ type: "IdentityServerClient.RedirectToLoginPage" }, "*");
        } else {
            this.internalSignOut();
        }
    }

    public clientInfoSettings(): ClientInfo | null {
        let info = sessionStorage.getItem(IdentityServerConfig.ClientInfoKey);
        if (info) {
            return JSON.parse(info);
        }
        return null;
    }

    getCurrentUserLogin(): Promise<string> {
        if (this.userManager == null) {
            return new Promise(resolve => resolve(""));
        }
        return this.userManager
            .getUser()
            .then(user => {
                if (user && user.profile && user.profile.profile) {
                    const userProfile: Profile = JSON.parse(user.profile.profile);
                    return userProfile.Login;
                }

                return "";
            })
            .catch(() => "");
    }

    getCurrentUserProfile(): Promise<Profile | null> {
        if (this.userManager == null) {
            return new Promise(resolve => resolve(null));
        }
        return this.userManager.getUser().then(user => {
            if (user && user.profile && user.profile.profile) {
                const userProfile: Profile = JSON.parse(user.profile.profile);
                return userProfile;
            }
            return null;
        });
    }

    start(settings: Partial<IdentityAuthOptions> = {}) {
        this.updateSettings();
        this.settings.userManagerOptions = Object.assign(
            this.settings.userManagerOptions || {},
            settings.userManagerOptions
        ) as UserManagerSettings;
        this.userManager = new UserManager(this.settings.userManagerOptions);
        this.initializeEvents();
        this.loadPermissions();
    }

    hasPermission(permission: string, resultCallback: (res: boolean) => void) {
        resultCallback(this.permissions.includes(permission));
    }

    hasPermissionAsync(permission: string): Promise<boolean> {
        if (!this.userManager) {
            return Promise.resolve(false);
        }

        return this.userManager
            .getUser()
            .then(user => {
                if (user && user.profile && user.profile.profile) {
                    const userProfile: Profile = JSON.parse(user.profile.profile);
                    const moduleId = this.clientInfoSettings()?.clientId;
                    const module = userProfile.UserModules.find(m => m.Id === moduleId);

                    if (module) {
                        return module.Permissions.includes(permission);
                    }

                    return false;
                }

                return false;
            })
            .catch(() => false);
    }

    hasPermissions<T>(...permissions: Extract<keyof T, string>[]): { [translation in keyof T]: boolean } {
        const createPermissionsResult = (userPermissions: string[] = []) =>
            Object.fromEntries(permissions.map(p => [p, userPermissions.includes(p) ?? false])) as {
                [translation in keyof T]: boolean;
            };

        return createPermissionsResult(this.permissions);
    }

    hasPermissionsAsync<T>(...permissions: Extract<keyof T, string>[]): Promise<{ [translation in keyof T]: boolean }> {
        const createPermissionsResult = (userPermissions: string[] = []) =>
            Object.fromEntries(permissions.map(p => [p, userPermissions.includes(p) ?? false])) as {
                [translation in keyof T]: boolean;
            };

        if (!this.userManager) {
            return Promise.resolve(createPermissionsResult());
        }

        return this.userManager
            .getUser()
            .then(user => {
                if (user && user.profile && user.profile.profile) {
                    const userProfile: Profile = JSON.parse(user.profile.profile);
                    const moduleId = this.clientInfoSettings()?.clientId;
                    const module = userProfile.UserModules.find(m => m.Id === moduleId);

                    return createPermissionsResult(module?.Permissions);
                }
                return createPermissionsResult();
            })
            .catch(createPermissionsResult);
    }

    private initializeEvents(): void {
        this.userManager!.events.addSilentRenewError(this.signIn.bind(this));
        this.userManager!.events.addUserSignedOut(this.signOut.bind(this));
        this.userManager!.events.addUserLoaded(this.userLoaded.bind(this));
    }

    private loadPermissions(): void {
        this.userManager!.getUser().then(user => {
            if (user && user.profile && user.profile.profile) {
                const userProfile: Profile = JSON.parse(user.profile.profile);
                const moduleId = this.clientInfoSettings()?.clientId;
                const module = userProfile.UserModules.find(m => m.Id === moduleId);

                this.permissions = module?.Permissions ?? [];
            }
        });
    }

    private internalSignOut() {
        this.userManager?.signoutRedirect({
            post_logout_redirect_uri: `${window.location.protocol}//${window.location.host}`
        });
    }

    private updateSettings(): void {
        const clientInfo = this.clientInfoSettings();

        this.settings.userManagerOptions = {
            response_type: "id_token token",
            scope: "openid " + clientInfo?.clientId,
            silent_redirect_uri: `${window.location.protocol}//${window.location.host}/silent_renew.html`,
            automaticSilentRenew: true,
            monitorSession: true,
            filterProtocolClaims: true,
            loadUserInfo: true,
            accessTokenExpiringNotificationTime: 100,
            revokeAccessTokenOnSignout: false,
            silentRequestTimeout: 30000,
            client_id: clientInfo?.clientId,
            redirect_uri: clientInfo?.clientUrl,
            authority: clientInfo?.authorityUrl
        };
    }
}

angular
    .module("topline.services")
    .service(IdSvrAuthService.componentName, [TokenService.componentName, IdSvrAuthService]);
